import { ElMessageBox } from "element-plus";
import { hardReload, startVersionCheck, type VersionInfo } from "/@/cool/utils/version-check";
import type { Router } from "vue-router";

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

function renderChangesHtml(changes: string[]): string {
	if (!changes.length) return "";
	const max = 10;
	const shown = changes.slice(0, max);
	const items = shown.map((c) => `<li>${escapeHtml(c)}</li>`).join("");
	const more = changes.length > max ? `<li>…等 ${changes.length} 条</li>` : "";
	return `<ul style="margin:8px 0 0;padding-left:1.2em;">${items}${more}</ul>`;
}

async function confirmReload(
	info: VersionInfo,
	reason: "risk" | "force-pending"
): Promise<boolean> {
	const title = info.publishType === "force" ? "发现强制更新版本" : "发现新版本，是否立即更新？";
	const tip =
		reason === "force-pending"
			? "当前仍有未完成的高风险请求（如提交/工单/快递/充值等），刷新可能导致结果异常。"
			: "检测到近期有高风险操作（新增修改、客诉重打/重修/重建、工单、快递、充值、红冲、导出购买订单等），建议确认后再刷新。";

	const content = `<p>${escapeHtml(info.message)}</p>${renderChangesHtml(info.changes || [])}<p style="color:#E6A23C;margin-top:8px;">${tip}</p>`;

	try {
		await ElMessageBox.confirm(content, title, {
			type: "warning",
			dangerouslyUseHTMLString: true,
			showCancelButton: info.publishType !== "force",
			cancelButtonText: "暂不更新",
			confirmButtonText: "立即更新",
			closeOnClickModal: false,
			closeOnPressEscape: info.publishType !== "force",
			showClose: info.publishType !== "force"
		});
		return true;
	} catch (err) {
		// 用户取消 / 关闭
		if (err === "cancel" || err === "close") {
			return false;
		}
		// 轻量入口未装 EP 样式时降级：只显示 message
		if (info.publishType === "force") {
			window.alert(`${info.message}\n\n请刷新页面以使用最新版本。`);
			return true;
		}
		return window.confirm(`${info.message}\n\n是否立即更新？`);
	}
}

export type StartClientVersionCheckOptions = {
	router?: Router;
	/** 默认：localhost:9001 为 silent */
	silent?: boolean;
	trackButtons?: boolean;
};

/** 客户端发版检测入口（version.json 信号 + API 提示配置） */
export function startClientVersionCheck(options: StartClientVersionCheckOptions = {}): void {
	startVersionCheck({
		silent: options.silent,
		router: options.router,
		trackButtons: options.trackButtons,
		onConfirmReload: confirmReload
	});
}

/** 供 router.onError 等场景复用 */
export { hardReload };
